import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core'
import { ISelectItem } from '@lla-platform/core/core-ui'
import { DateService } from '@lla-platform/core/core-util'
import { UntilDestroy } from '@ngneat/until-destroy'
import { Dictionary, groupBy, isEqual, sortBy, uniq } from 'lodash'
import moment from 'moment'

@UntilDestroy()
@Component({
  selector: 'lla-week-selector',
  templateUrl: './week-selector.component.html',
  styleUrls: ['./week-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class WeekSelectorComponent implements OnInit, AfterViewInit {
  @ViewChild('weekSelectorContent') weekSelectorContent: ElementRef<HTMLDivElement>
  @Input() singleSelection = false
  @Input() includeFuture = false
  @Input() selectedWeeks: number[] = []
  selectedWeeksValue: number[] = []

  @Output() canceld = new EventEmitter()
  @Output() selectedWeeksChanged = new EventEmitter<number[]>()

  allWeeks: ISelectItem[] = []
  allWeeksGrouped: Dictionary<ISelectItem[]> = {}
  lastSelectedWeek?: number
  inRangeWeeks: number[] = []
  isShiftPressed = false
  currentHoveredWeek?: number

  periodItems: ISelectItem[] = [
    { label: 'Last Week', value: 1 },
    { label: 'Last 4 Weeks', value: 4 },
    { label: 'Last 12 Weeks', value: 12 },
    { label: 'Last 52 weeks', value: 52 }
  ]

  selectedPeriod?: number

  constructor(private dateService: DateService) {}

  ngOnInit(): void {
    this.selectedWeeksValue = [...this.selectedWeeks]
    this.populateAllWeeks()
    this.detectPeriodFromSelectedWeeks()
  }

  ngAfterViewInit(): void {
    this.scrollToView()
  }

  private scrollToView(): void {
    if (this.weekSelectorContent?.nativeElement) {
      const container = this.weekSelectorContent.nativeElement

      if (this.selectedWeeksValue.length > 0) {
        const weekItem = document.getElementById(
          `week-item-${this.selectedWeeksValue[this.selectedWeeksValue.length - 1]}`
        )
        if (weekItem) {
          container.scrollTop = weekItem.offsetTop - 150
        }
        return
      }

      setTimeout(() => {
        container.scrollTop = container.scrollHeight
      }, 0)
    }
  }

  private populateAllWeeks(): void {
    this.allWeeks = this.dateService.getAllWeeks(this.includeFuture)
    this.allWeeksGrouped = groupBy(this.allWeeks, 'extraInfo') as Dictionary<ISelectItem[]>
  }

  setSelectedPeriod(item: ISelectItem) {
    this.selectedPeriod = item.value
    this.lastSelectedWeek = undefined
    this.selectedWeeksValue = this.getLastNWeeks(item.value)
    this.scrollToView()
  }

  selectWeek(week: number) {
    if (this.singleSelection) {
      this.selectedWeeksValue = [week]
      this.selectedWeeksChanged.emit(this.selectedWeeksValue)
      return
    }

    if (this.inRangeWeeks.length > 0) {
      this.selectedWeeksValue = uniq([...this.selectedWeeksValue, ...this.inRangeWeeks])
      this.lastSelectedWeek = week
    } else {
      if (this.selectedWeeksValue.includes(week)) {
        this.selectedWeeksValue = this.selectedWeeksValue.filter((el) => el !== week)
      } else {
        this.selectedWeeksValue.push(week)
        this.lastSelectedWeek = week
      }
    }

    if (this.selectedWeeksValue.length === 0) {
      this.lastSelectedWeek = undefined
    }

    this.detectPeriodFromSelectedWeeks()
  }

  @HostListener('window:keydown.shift')
  onShiftKeyDown() {
    this.isShiftPressed = true
    this.updateRangeWeeks()
  }

  @HostListener('window:keyup.shift')
  onShiftKeyUp() {
    this.isShiftPressed = false
    this.updateRangeWeeks()
  }

  onWeekMouseEnter(week: number, event: MouseEvent) {
    this.currentHoveredWeek = week
    this.isShiftPressed = event.shiftKey
    this.updateRangeWeeks()
  }

  onWeekMouseLeave() {
    this.currentHoveredWeek = undefined
    this.inRangeWeeks = []
  }

  applySelection() {
    if (this.selectedWeeksValue.length === 0) {
      return
    }
    this.selectedWeeksChanged.emit(sortBy(this.selectedWeeksValue))
  }

  clearAll() {
    this.selectedWeeksValue = []
    this.lastSelectedWeek = undefined
    this.detectPeriodFromSelectedWeeks()
  }

  private updateRangeWeeks() {
    if (
      this.isShiftPressed &&
      this.lastSelectedWeek !== undefined &&
      this.currentHoveredWeek !== undefined
    ) {
      this.inRangeWeeks = this.getWeeksInRange(this.lastSelectedWeek, this.currentHoveredWeek)
    } else {
      this.inRangeWeeks = []
    }
  }

  private getWeeksInRange(week1: number, week2: number): number[] {
    const index1 = this.allWeeks.findIndex((w) => w.value === week1)
    const index2 = this.allWeeks.findIndex((w) => w.value === week2)

    if (index1 === -1 || index2 === -1) {
      return []
    }

    const startIndex = Math.min(index1, index2)
    const endIndex = Math.max(index1, index2)

    return this.allWeeks.slice(startIndex, endIndex + 1).map((week) => week.value)
  }

  private detectPeriodFromSelectedWeeks(): void {
    if (this.selectedWeeksValue.length === 0) {
      this.selectedPeriod = undefined
      return
    }

    // Check each period option
    for (const period of this.periodItems) {
      const expectedWeeks = this.getLastNWeeks(period.value)

      // Check if selectedWeeksValue matches this period
      if (
        this.selectedWeeksValue.length === expectedWeeks.length &&
        isEqual(sortBy(this.selectedWeeksValue), sortBy(expectedWeeks))
      ) {
        this.selectedPeriod = period.value
        return
      }
    }

    // If no match found, clear selectedPeriod
    this.selectedPeriod = undefined
  }

  private getLastNWeeks(n: number): number[] {
    // Get last n weeks from allWeeks (excluding current week)
    return this.allWeeks
      .slice(this.allWeeks.length - n - 1, this.allWeeks.length - 1)
      .map((week) => week.value)
  }
}
